import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { jws } from 'jsrsasign';

import { Observable, of, Subject } from 'rxjs';
import { first, flatMap, map, tap } from 'rxjs/operators';
import { CookieAdapterService } from '../../core/cookie-adapter.service';
import { OeAppService } from '../oe-app.service';
import SpeechaceReportDto from '../types/SpeechaceReportDto';
import { SpeechaceTokenDto } from '../types/SpeechaceTokenDto';
import { SpeechaceModalComponent } from './speechace-modal.component';
import { ProgressResultDto } from '../types/ProgressResultDto';
import { ImmersionCourseService } from '../immersion-course.service';
import { OeAppPersonService } from '../oe-app-person.service';
import { CourseContentType } from '../types/course-content-type';

const SPEECHACE_COOKIE = '__oe_speechace';

@Injectable()
export class SpeechaceService {
  progressReported = new Subject<number>();

  constructor(
    private http: HttpClient,
    private cookieService: CookieAdapterService,
    private oeApp: OeAppService,
    private oeAppPersonService: OeAppPersonService,
    private modalService: NgbModal,
    private immersionCourseService: ImmersionCourseService
  ) {}

  getHostUrl(): string {
    return this.oeApp.getData('common').speechaceHostname;
  }

  private isTokenValid(token: string): boolean {
    if (!token) {
      return false;
    }

    try {
      const { payloadObj } = jws.JWS.parse(token);
      const contactId = this.oeAppPersonService.contactId;
      return (
        new Date().getTime() / 1000 < payloadObj.exp &&
        contactId === payloadObj.userID
      );
    } catch (e) {
      console.error(e);
      return false;
    }

  }

  getToken(forceFetch: boolean = false): Observable<string> {
    const token = !forceFetch && this.cookieService.get(SPEECHACE_COOKIE);
    if (!this.isTokenValid(token)) {
      return this.fetchToken();
    }

    return of(token).pipe(first());
  }

  openActivity(
    title: string,
    courseId: number,
    activityId: string,
    isComplete: boolean
  ) {
    const modalRef = this.modalService.open(SpeechaceModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.activityId = activityId;
    modalRef.componentInstance.courseId = courseId;
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.isComplete = isComplete;
  }

  fetchToken(): Observable<string> {
    const data = { tokenType: 'JWT', personId: this.oeAppPersonService.id };
    return this.http.post<SpeechaceTokenDto>(`${this.oeApp.thirdPartyServiceHost}/v1/speechace-tokens`, data).pipe(
      map((result) => {
        this.cookieService.put(SPEECHACE_COOKIE, result.accessToken);

        return result.accessToken;
      })
    );
  }

 retrieveProgress(
    activityId: number,
    sessionStartTime?: number
  ): Observable<SpeechaceReportDto> {
    const hostUrl = this.getHostUrl();
    return this.getToken().pipe(
      flatMap((token) =>
        this.http.get<SpeechaceReportDto>(
          `${hostUrl}/embed/api/${activityId}/report?speechace_token=${token}&session_start_time=${sessionStartTime}`
        )
      )
    );
  }

  reportProgressForCourse(
    courseId: number,
    activityId: number,
    sessionStartTime: number,
    ignoreCompletion: boolean = false
  ): Observable<ProgressResultDto> {
    return this.retrieveProgress(activityId, sessionStartTime).pipe(
      flatMap((progress) => {
        const sessionTimeInSeconds = Math.floor(
          progress.report.sessionTime / 1000.0
        );
        // HACK: Do not mark it as complete if it has been completed before
        const isComplete = !ignoreCompletion && !!progress.report.completion;

        return this.immersionCourseService
          .savePersonCourse(sessionTimeInSeconds, isComplete, courseId, CourseContentType.PRONUNCIATIONS)
          .pipe(tap(() => this.progressReported.next(courseId)));
      })
    );
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/speechace-modal/speechace.service.ts